import {
    defaultBaseQuery,
    transformErrorResponse,
    transformSuccessResponse,
} from "@/services";
import { createApi } from "@reduxjs/toolkit/query/react";

/**
 * dashboardAPI
 * RTK Query slice for Administration Dashboard data.
 *
 * Endpoints:
 *  - getDashboardSummary  → GET /dashboard/summary
 *
 * Response shape (expected from backend):
 * {
 *   totalHoursWeek:  "42h 30m",
 *   totalHoursMonth: "142h 30m",
 *   overtimeMonth:   "2h 30m",
 *   attendanceRate:  "42%",
 *   attendanceReport: {
 *     totalDays: 22,
 *     presentDays: 20,
 *     absentDays: 1,
 *     lateComing: 1,
 *     earlyGoing: 0
 *   },
 *   leaveBalance: {
 *     casual:          { remaining: 8, total: 12 },
 *     privilege:       { remaining: 10, total: 12 },
 *     sick:            { remaining: 8, total: 12 },
 *     leaveWithoutPay: { remaining: 8, total: 12 }
 *   },
 *   recentAttendance: [
 *     { date: "15 May", checkIn: "09:43 AM", checkOut: "06:43 PM", status: "Present" },
 *     ...
 *   ],
 *   recentLeaveRequests: [
 *     { leaveType: "Casual Leave", dateRange: "15 May 2025", totalDays: 1, status: "Approved" },
 *     ...
 *   ]
 * }
 */
const dashboardAPI = createApi({
    reducerPath: "dashboardAPI",
    baseQuery: defaultBaseQuery,
    tagTypes: ["DashboardSummary", "EmployeeDashboard", "AttendanceHistory", "LeaveHistory", "EmployeeAttendanceSummary"],
    endpoints: (builder) => ({
        getDashboardSummary: builder.query({
            query: () => ({
                url: `/dashboard/summary`,
                method: "GET",
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
            providesTags: ["DashboardSummary"],
        }),
        getEmployeeDashboard: builder.query({
            query: () => ({
                url: `/dashboard/employee/counts`,
                method: "GET",
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
            providesTags: ["EmployeeDashboard"],
        }),
        getAttendanceHistory: builder.query({
            query: (isMonth = true) => ({
                url: `/attendance/history?isMonth=${isMonth}`,
                method: "GET",
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
            providesTags: ["AttendanceHistory"],
        }),
        getLeaveHistory: builder.query({
            query: (isMonth = true) => ({
                url: `/leave/history?isMonth=${isMonth}`,
                method: "GET",
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
            providesTags: ["LeaveHistory"],
        }),
        getEmployeeAttendanceSummary: builder.query({
            query: (month) => ({
                url: `/dashboard/employee-attendance/summary`,
                method: "GET",
                params: { month },
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
            providesTags: ["EmployeeAttendanceSummary"],
        }),
    }),
});

export const {
    useLazyGetDashboardSummaryQuery,
    useGetDashboardSummaryQuery,
    useGetEmployeeDashboardQuery,
    useGetAttendanceHistoryQuery,
    useGetLeaveHistoryQuery,
    useGetEmployeeAttendanceSummaryQuery,
} = dashboardAPI;

export default dashboardAPI;
